import React, { useMemo } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { ClosingPackageProjection, ClosingPackageTaskSummary } from '../types';
import {
  countRemainingClosingPackageTasks,
  selectPrimaryClosingPackageTask,
} from '../lib/activeTaskSelector';
import {
  isConfirmClientReceiptTask,
  resolveConfirmClientReceiptTaskDisplay,
} from '../lib/confirmClientReceiptTaskDisplay';
import { TASK_TYPE_LABELS } from '../../../constants/controlTasks';
import './closingPackageWorkspace.css';

const { Text } = Typography;

export type ClosingPackageActiveTaskInfoProps = {
  applicationId: number;
  projection: ClosingPackageProjection;
};

const ACTIVE_STATUS_META: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Открыта', color: 'blue' },
  IN_PROGRESS: { label: 'В работе', color: 'processing' },
  OVERDUE: { label: 'Просрочена', color: 'error' },
  PENDING: { label: 'Ожидает', color: 'default' },
};

function collectProjectionTasks(projection: ClosingPackageProjection): ClosingPackageTaskSummary[] {
  const byId = new Map<number, ClosingPackageTaskSummary>();

  for (const task of projection.blockers ?? []) {
    byId.set(task.id, task);
  }
  for (const item of projection.items ?? []) {
    for (const task of item.openTasks ?? []) {
      byId.set(task.id, task);
    }
  }

  return Array.from(byId.values());
}

function formatDueDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function taskTitle(taskType: string): string {
  const key = String(taskType || '').trim();
  const upper = key.toUpperCase();
  const closingLabels: Record<string, string> = {
    SEND_CLOSING_PACKAGE_SCANS: 'Отправить сканы комплекта клиенту',
    SEND_SCANS_TO_CLIENT: 'Отправить сканы клиенту',
    UPLOAD_TN_ORIGINAL: 'Загрузить оригиналы ТН/ТТН',
    ORIGINAL_TRANSPORT_DOC_MISSING: 'Отсутствуют оригиналы ТН/ТТН',
    CONFIRM_CLIENT_RECEIPT: 'Подтвердить получение оригиналов комплекта',
    PREPARE_ORIGINALS: 'Подготовить оригиналы',
    SEND_ORIGINALS: 'Отправить оригиналы комплекта клиенту',
    SEND_VIA_EDO: 'Отправить документы через ЭДО',
    CONFIRM_EDO_RECEIPT: 'Подтвердить получение документов по ЭДО',
  };
  return (
    closingLabels[upper] ||
    TASK_TYPE_LABELS[key] ||
    TASK_TYPE_LABELS[upper] ||
    key ||
    'Задача'
  );
}

function statusMeta(status: string | null | undefined) {
  const key = String(status || '').trim().toUpperCase();
  return ACTIVE_STATUS_META[key] ?? { label: status || '—', color: 'default' };
}

/**
 * Informational Active Task strip (FE-002A Architect Review).
 * Display-only — no task execution, no ERP Modal, no Action Bar.
 * Navigation: «Все задачи заявки» → application tasks tab.
 */
const ClosingPackageActiveTaskInfo: React.FC<ClosingPackageActiveTaskInfoProps> = ({
  applicationId,
  projection,
}) => {
  const tasks = useMemo(() => collectProjectionTasks(projection), [projection]);
  const primary = useMemo(() => selectPrimaryClosingPackageTask(tasks), [tasks]);
  const receiptDisplay = useMemo(() => {
    if (!primary || !isConfirmClientReceiptTask(primary.taskType)) {
      return null;
    }

    return resolveConfirmClientReceiptTaskDisplay(
      primary,
      new Date(),
      projection.workflowMetadata?.originalsEstimatedReceiptAt ?? primary.dueDate
    );
  }, [primary, projection.workflowMetadata?.originalsEstimatedReceiptAt]);
  const remaining = useMemo(
    () => countRemainingClosingPackageTasks(tasks, primary?.id ?? null),
    [tasks, primary?.id]
  );

  const tasksTabPath = `/applications/${applicationId}?tab=tasks`;

  return (
    <div className="cp-active-task cp-span-full" data-testid="closing-package-active-task-info">

      <div className="cp-active-task__kicker">Активная задача</div>

      {primary ? (
        <>
          <Text strong className="cp-active-task__title">
            {taskTitle(primary.taskType)}
          </Text>

          <div className="cp-active-task__meta">
            <div className="cp-active-task__meta-item">
              <Text type="secondary" className="cp-active-task__label">
                Ответственный
              </Text>
              <Text>—</Text>
            </div>
            {receiptDisplay ? (
              <>
                <div className="cp-active-task__meta-item">
                  <Text type="secondary" className="cp-active-task__label">
                    Ожидаемая дата получения
                  </Text>
                  <Text>{receiptDisplay.estimatedReceiptAtLabel}</Text>
                </div>
                <div className="cp-active-task__meta-item">
                  <Text type="secondary" className="cp-active-task__label">
                    Статус
                  </Text>
                  <Tag color={receiptDisplay.statusColor}>{receiptDisplay.statusLabel}</Tag>
                </div>
              </>
            ) : (
              <>
                <div className="cp-active-task__meta-item">
                  <Text type="secondary" className="cp-active-task__label">
                    Срок
                  </Text>
                  <Text>{formatDueDate(primary.dueDate)}</Text>
                </div>
                <div className="cp-active-task__meta-item">
                  <Text type="secondary" className="cp-active-task__label">
                    Статус
                  </Text>
                  <Tag color={statusMeta(primary.status).color}>{statusMeta(primary.status).label}</Tag>
                </div>
              </>
            )}
          </div>

          <div className="cp-active-task__footer">
            {remaining > 0 ? (
              <Tag className="cp-active-task__more">Ещё {remaining} по заявке</Tag>
            ) : (
              <Text type="secondary" className="cp-active-task__more-empty">
                Других активных задач комплекта нет
              </Text>
            )}
            <Link to={tasksTabPath}>
              <Button type="default" size="small">
                Все задачи заявки
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text type="secondary">Нет активных задач, связанных с комплектом.</Text>
          <Link to={tasksTabPath}>
            <Button type="default" size="small">
              Все задачи заявки
            </Button>
          </Link>
        </Space>
      )}
    </div>
  );
};

export default ClosingPackageActiveTaskInfo;
